import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Table } from "reactstrap";

import { getJobTimeEntryList } from "../../store/singleJob/action";
import { getProductListNewApiCall } from "../../store/products/action";
import HandleDateTime from "./HandleDateTime";

// Minutes -> "1h 10m" (or "0m"); accepts string or number.
const minsToHm = (mins) => {
  const total = Math.round(Number(mins) || 0);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

// Read-only breakdown of the time entries recorded against a job, grouped by
// Product (accordion) and split by person. Used alongside the billable-items
// list on the job completion review flow.
export default function JobTimeEntriesByProduct({ jobId }) {
  const dispatch = useDispatch();

  const { entries, productList } = useSelector((state) => ({
    entries: state.SingleJob?.jobTimeEntryList?.jobtimeentry_list,
    productList: state.Products.productsListNewApiCall?.product_list,
  }));

  useEffect(() => {
    if (!jobId) return;
    dispatch(getJobTimeEntryList([{ job_data: { Id: jobId } }]));
  }, [jobId]);

  useEffect(() => {
    if (isEmpty(productList)) {
      dispatch(getProductListNewApiCall());
    }
  }, []);

  const productNameById = useMemo(() => {
    const map = {};
    (productList || []).forEach((p) => {
      map[p.Id] = p.Name || p.NameCustomerFacing || p.Title || `Product #${p.Id}`;
    });
    return map;
  }, [productList]);

  // Group by Product_Id -> Staff_Id.
  const grouped = useMemo(() => {
    const byProduct = {};
    (entries || []).forEach((e) => {
      const pid = e.Product_Id || "none";
      if (!byProduct[pid]) {
        byProduct[pid] = {
          productId: e.Product_Id,
          totalMins: 0,
          count: 0,
          byStaff: {},
        };
      }
      const group = byProduct[pid];
      const mins = Number(e.DurationMins) || 0;
      group.totalMins += mins;
      group.count += 1;

      const sid = e.Staff_Id || "none";
      if (!group.byStaff[sid]) {
        group.byStaff[sid] = {
          staffId: e.Staff_Id,
          staffName: e.Staff_FullName || "Unknown",
          totalMins: 0,
          entries: [],
        };
      }
      group.byStaff[sid].totalMins += mins;
      group.byStaff[sid].entries.push(e);
    });
    return Object.values(byProduct).sort((a, b) => b.totalMins - a.totalMins);
  }, [entries]);

  if (isEmpty(entries)) {
    return (
      <div className="text-center text-muted py-3">
        No time entries recorded against this job.
      </div>
    );
  }

  return (
    <div>
      {grouped.map((group) => {
        const productName =
          productNameById[group.productId] ||
          (group.productId ? `Product #${group.productId}` : "No product");
        const staffGroups = Object.values(group.byStaff).sort(
          (a, b) => b.totalMins - a.totalMins
        );
        return (
          <Accordion key={group.productId || "none"} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", pr: 1 }}
              >
                <Typography sx={{ fontWeight: 600 }}>{productName}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={`${group.count} entr${group.count === 1 ? "y" : "ies"}`} />
                  <Chip
                    size="small"
                    color="primary"
                    label={minsToHm(group.totalMins)}
                  />
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {staffGroups.map((staff) => (
                <div key={staff.staffId || "none"} className="mb-3">
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    className="mb-1"
                  >
                    <Typography sx={{ fontWeight: 600 }} variant="body2">
                      {staff.staffName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {minsToHm(staff.totalMins)}
                    </Typography>
                  </Stack>
                  <Table size="sm" borderless className="mb-0">
                    <tbody>
                      {staff.entries
                        .slice()
                        .sort(
                          (a, b) =>
                            new Date(a.StartDateTime) - new Date(b.StartDateTime)
                        )
                        .map((entry) => (
                          <tr key={entry.Id}>
                            <td className="text-muted">
                              {entry.StartDateTime
                                ? HandleDateTime(entry.StartDateTime)
                                : "--"}
                            </td>
                            <td className="text-end">
                              {minsToHm(entry.DurationMins)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}
